import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { web } from "@kikoff/proto/src/protos";
import { format } from "@kikoff/utils/src/string";

import { UnderlineButton } from "@component/buttons/buttons";
import PageAlign from "@component/dashboard/offer/page_align";
import SectionCard from "@component/dashboard/offer/section_card";
import ViewTemplate from "@component/dashboard/offer/view_template";
import WithFooter from "@component/dashboard/offer/with_footer";
import ReviewAutorenewSection from "@component/dashboard/store/review_autorenew_section";
import PersistentAppBar from "@component/flows/components/PersistentAppBar";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import Line from "@component/line";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import ProductEntry from "@component/shopping/product_entry";
import { shopCheckout } from "@feature/shopping";
import { renewSubscriptionAndCreditLine } from "@feature/subscription";
import {
  getKikoffCreditAccountMentalModelName,
  productData,
} from "@src/constants";
import { track } from "@util/analytics";

import kikoffCardIcon from "./images/credit_account.svg";

import styles from "./review_order.module.scss";

interface CreditlineIncreaseOfferProps {
  preview: web.public_.ICheckoutPreviewResponse;
}

const CreditlineIncreaseOffer: React.FC<CreditlineIncreaseOfferProps> = ({
  preview,
}) => {
  return (
    <SectionCard
      className={styles["creditline-increase-offer"]}
      header="Credit limit offer"
    >
      <span className={styles["limit-title"]}>Current Credit Limit</span>
      <span className={styles["limit-value"]}>
        {format.money(preview.creditLimitCents)}
      </span>

      <span className={styles["limit-title"]}>After Today’s Purchase</span>
      <span className={styles["limit-value"]}>
        {format.money(preview.newCreditLimitCents)}
      </span>
    </SectionCard>
  );
};

interface OrderReviewProps extends PageRegistration {
  isRenewal?: boolean;
}

export default function OrderReview({
  page,
  configFlow,
  Button,
  isRenewal = false,
}: OrderReviewProps) {
  const dispatch = useDispatch();

  const email = useSelector((state) => state.user.proto.info.email);
  const creditLine = useSelector((state) => state.creditLine.account);
  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const productType = preview?.order.orderItems[0].product.type;
  const product = useSelector((state) => state.shopping.bag.items[0]?.product);
  const displayCreditlineIncreaseOffer =
    preview && preview.newCreditLimitCents !== preview.creditLimitCents;

  configFlow.layout(PersistentAppBar, { title: "Review your order" }).button(
    !creditLine ? (
      <Button>Review Credit Agreement</Button>
    ) : (
      <Button
        onClick={() => {
          if (isRenewal) {
            dispatch(
              renewSubscriptionAndCreditLine(product.plan, product.token)
            );
          } else {
            dispatch(shopCheckout());
          }
        }}
        text={{
          pending: "Checking out...",
          success: "Checked out",
        }}
      >
        Confirm purchase
      </Button>
    )
  );

  const orderSummary = preview && (
    <OrderSummary items={preview.order.orderItems}>
      <UnderlineButton
        className={styles.switch}
        onClick={() => {
          track.tap("Store - Switch Item", {
            "screen name": "review",
          });
          page.controller.prev();
        }}
      >
        Change plan
      </UnderlineButton>
    </OrderSummary>
  );

  const note = !creditLine && "You’re almost ready to start building credit.";

  const footer = (
    <PageAlign wrap>
      <footer>
        This purchase will be financed by your{" "}
        {getKikoffCreditAccountMentalModelName()}
        {!creditLine && " once you accept the agreements on the following page"}
        .
      </footer>
    </PageAlign>
  );

  return (
    <WithFooter className={styles["order-review"]} footer={footer}>
      <PageAlign>
        <ViewTemplate note={note}>
          {displayCreditlineIncreaseOffer && (
            <CreditlineIncreaseOffer preview={preview} />
          )}
          <PaymentMethod />
          {orderSummary}
          <div className={styles.email}>
            {productData[productType]?.deliveryInstructions({ email })}
          </div>
        </ViewTemplate>
      </PageAlign>
    </WithFooter>
  );
}

interface PaymentMethodProps {}

interface PaymentBreakdownProps {
  preview: web.public_.ICheckoutPreviewResponse;
}

const PaymentBreakdown: React.FC<PaymentBreakdownProps> = ({ preview }) => {
  if (!preview) return <GenericSpinner center size={30} />;

  const creditLine = useSelector((state) => state.creditLine.account);

  const term = preview.paymentSchedule?.repaymentTerm;

  const pricePerMonth = format.money(
    preview?.creditLineAgreement.minimumPaymentAmountCents,
    { includeCents: true }
  );

  const note = `With this purchase, you’ll be ready to build ${
    !creditLine ? "" : "an additional"
  } ${term - (creditLine?.loanDurationMonths || 0)} months of payment history ${
    term > 10
      ? "by paying the monthly minimum on your balance"
      : `at just ${pricePerMonth}/month`
  }.`;

  const creditLimitChange =
    preview.newCreditLimitCents !== preview.creditLimitCents;

  const creditLimitTitle = creditLimitChange
    ? "New credit limit with today's purchase"
    : "Credit Limit";

  return (
    <div className={styles["payment-breakdown"]}>
      <div className="note">
        Here’s how this will impact your{" "}
        {getKikoffCreditAccountMentalModelName()}
      </div>
      <div className={styles.breakdown}>
        <div>
          <header>{creditLimitTitle}</header>
          <span>{format.money(preview.newCreditLimitCents)}</span>
        </div>
        <div>
          <header>Starting balance</header>
          <span>{format.money(preview.creditBalanceCents)}</span>
        </div>
        <div>
          <header>Purchase Total</header>
          <span>{format.money(preview.order.totalPriceCents)}</span>
        </div>
        <Line className={styles.line} />
        <div>
          <header>New account balance</header>
          <span>{format.money(preview.newCreditBalanceCents)}</span>
        </div>
        <div>
          <header>New credit utilization</header>
          <span>{previewUtilizationString(preview)}</span>
        </div>
      </div>
      <Line className={styles.line} />
      <div className={styles.tip}>
        <header>CREDIT TIP ✨</header>

        <div className="note">
          {note}
          {preview.warnings?.map((warning, i) => (
            <div className={`${styles.warning} color:red-500`} key={i}>
              {warning}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function previewUtilizationString(preview) {
  const beforePercentage = Math.round(
    (preview.newCreditBalanceCents * 100) / preview.creditLimitCents
  );
  const afterPercentage = Math.round(
    (preview.newCreditBalanceCents * 100) / preview.newCreditLimitCents
  );

  // adding 1 decimal place if the before and after utilization percentages are
  // the same.
  const precision = beforePercentage === afterPercentage ? 1 : 0;
  const creditUtilizationPercent = (
    (preview.newCreditBalanceCents * 100) /
    preview.newCreditLimitCents
  ).toFixed(precision);
  return `${creditUtilizationPercent}% (${format.money(
    preview.newCreditBalanceCents
  )} / ${format.money(preview.newCreditLimitCents)})`;
}

const PaymentMethod: React.FC<PaymentMethodProps> = () => {
  const preview = useSelector((state) => state.shopping.checkoutPreview);

  return (
    <SectionCard
      className={styles["payment-method"]}
      header="Payment Method"
      flat
    >
      <div className="note">
        You’ll be paying with your {getKikoffCreditAccountMentalModelName()}.
      </div>
      <img className={styles["kikoff-card"]} src={kikoffCardIcon.src} />
      <span>
        <b>{getKikoffCreditAccountMentalModelName()}</b>
      </span>
      <Line className={styles.line} />
      <PaymentBreakdown preview={preview} />
    </SectionCard>
  );
};

function formattedPurchaseTotal(items) {
  const purchaseTotal = items.reduce((total, item) => {
    return total + item.product.priceCents;
  }, 0);

  return format.money(purchaseTotal);
}

interface OrderSummaryProps {
  items: web.public_.IOrderItem[];
  children?: React.ReactNode;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  children,
}) => {
  const autorenewFeatureEnabled = useSelector((state) => {
    return !!state.shopping.checkoutPreview?.autoRenew;
  });

  const product = items[0]?.product;

  return (
    <SectionCard
      className={styles["order-summary"]}
      header="Order Summary"
      flat
    >
      {items[0] && (
        <ProductEntry widen product={product} className={styles.product} />
      )}
      {children}
      <Line spacing={24} />
      <div className={styles.breakdown}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          <header>Purchase total</header>
          <span>{formattedPurchaseTotal(items)}</span>
        </div>
      </div>
      <Line spacing={24} />
      {autorenewFeatureEnabled && <ReviewAutorenewSection product={product} />}
    </SectionCard>
  );
};
